import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import loadable from '@loadable/component';
import { useSpring } from 'react-spring';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useScrollDirection from 'utils/hooks/useScrollDirection';
import { ScheduleAnAppointmentModal } from 'components/components/molecules';
import SubNavigationContactModal from 'components/components/molecules/SubNavigationContactModal';
import { SubNavigationFloatingBar } from 'components/components/organisms';
import { useUiValue, openRegistrationModal } from 'stores/uiContext';
import { getDefaultFontSize } from 'utils/getDefaultFontSize';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import { getSalesCenterAddress, getSalesCenterMapHref } from 'utils/getMapLink';
import pageTypes from 'utils/pageTypes';

const ContactBarOrganism = loadable(() =>
  import('components/components/organisms/ContactBar'),
);
const getPlaceholderFields = (rendering, placeholder) => {
  try {
    const fields =
      rendering.placeholders &&
      rendering.placeholders[placeholder] &&
      rendering.placeholders[placeholder][0] &&
      rendering.placeholders[placeholder][0].fields;
    return fields || {};
  } catch (e) {
    console.log(e);
    return {};
  }
};
const ContactBar = ({ fields, rendering }) => {
  const result = getFieldValue(fields, 'result') || {};
  const {
    salesCenterVM,
    secondSalesCenterVM,
    scheduleAppointmentCtaVM,
    marketSummaryVM,
  } = result;

  const titleCtaVM = getPlaceholderFields(rendering, 'contactbar-titlecta');

  const firstCtaLink = getFieldValue(titleCtaVM, 'firstCtaLink') || {};
  const secondCtaLink = getFieldValue(titleCtaVM, 'secondCtaLink') || {};
  const title = getFieldValue(titleCtaVM, 'title') || '';
  const [
    {
      notificationActive,
      notificationHeight,
      nationalNotificationHeight,
      hasScheduleAppointmentCTA,
      subNavigationBarGeneralData,
    },
    dispatch,
  ] = useUiValue();
  const scrollDirection = useScrollDirection();
  const [fixedBarVisible, setFixedBarVisible] = useState(false);
  const [isScheduleAppointmentModalOpen, setIsScheduleAppointmentModalOpen] =
    useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isContactBarVisible, setIsContactBarVisible] = useState(false);
  const [defaultFontSize, setDefaultFontSize] = useState();
  const [notificationTopHeight, setNotificationTopHeight] = useState(0);
  const animatedRef = useRef();
  const contactBarRef = useRef();
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);
  const heroTitle =
    subNavigationBarGeneralData && subNavigationBarGeneralData.title;
  const heroButtonCta =
    subNavigationBarGeneralData && subNavigationBarGeneralData.buttonCta;
  const stickyCta = heroButtonCta && heroButtonCta.text ? heroButtonCta : null;
  const scheduleAppointmentCTA =
    salesCenterVM && salesCenterVM.scheduleAppointment
      ? { text: salesCenterVM.scheduleAppointment }
      : null;
  const address = getSalesCenterAddress(salesCenterVM);
  const mapHref = getSalesCenterMapHref(salesCenterVM);
  const phoneNumberCall = salesCenterVM && salesCenterVM.phoneNumberCall;
  const phoneHref = phoneNumberCall && phoneNumberCall.replace(/[^0-9+]/g, '');
  const email = salesCenterVM && salesCenterVM.email;
  const floatingBarVisible =
    fixedBarVisible && !(isMobile && isContactBarVisible);

  useEffect(() => {
    const font = getDefaultFontSize();
    setDefaultFontSize(font);
  }, []);

  useEffect(() => {
    setNotificationTopHeight(notificationHeight + nationalNotificationHeight);
  }, [notificationHeight, nationalNotificationHeight]);

  const fixedBarStyle = useSpring({
    config: { duration: 100 },
    top: floatingBarVisible
      ? scrollDirection === 'up'
        ? defaultFontSize <= 18
          ? notificationTopHeight + 110 // 80
          : notificationTopHeight + 150
        : defaultFontSize <= 18
        ? notificationTopHeight
        : notificationTopHeight + 70
      : -110, // 100
    backgroundColor: '#fff',
  });

  const handleOnScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const offset = notificationActive ? 360 : 300;
    if (currentScrollY > offset) {
      setFixedBarVisible(true);
    } else if (fixedBarVisible) {
      setFixedBarVisible(false);
    }
  }, [fixedBarVisible, notificationActive]);

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  useEffect(() => {
    if (!isMobile) {
      setIsContactBarVisible(false);
      return undefined;
    }

    const contactBarElement = contactBarRef.current;
    if (
      !contactBarElement ||
      typeof window === 'undefined' ||
      typeof window.IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsContactBarVisible(entry.isIntersecting);
    });

    observer.observe(contactBarElement);

    return () => observer.disconnect();
  }, [isMobile]);

  const handleCTAClick = () => {
    trackAnalyticsEvent({
      gtagEvent: 'StickyNavFormClick',
      mouseflowTag: 'StickyNavForm',
    });
    dispatch(openRegistrationModal());
  };

  const handleScheduleAppointmentCTAClick = () => {
    trackAnalyticsEvent({
      gtagEvent: 'StickyNavApptClick',
      mouseflowTag: 'StickyNavAppt',
    });
    setIsScheduleAppointmentModalOpen(true);
  };

  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  if (!salesCenterVM && !marketSummaryVM) return null;

  return (
    <>
      {subNavigationBarGeneralData &&
        subNavigationBarGeneralData.currentPageType &&
        subNavigationBarGeneralData.currentPageType === pageTypes.MpcPage && (
          <>
            {hasScheduleAppointmentCTA &&
              scheduleAppointmentCTA &&
              scheduleAppointmentCtaVM && (
                <ScheduleAnAppointmentModal
                  isOpen={isScheduleAppointmentModalOpen}
                  onClose={() => setIsScheduleAppointmentModalOpen(false)}
                  fields={scheduleAppointmentCtaVM}
                />
              )}
            {isMobile && salesCenterVM && (
              <SubNavigationContactModal
                isOpen={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
                salesCenter={salesCenterVM}
                scheduleAppointment={scheduleAppointmentCtaVM}
                communityTitle={heroTitle}
              />
            )}
            {salesCenterVM && (
              <SubNavigationFloatingBar
                title={heroTitle}
                address={address}
                mapHref={mapHref}
                phoneNumberCall={phoneNumberCall}
                phoneHref={phoneHref}
                email={email}
                cta={stickyCta}
                scheduleAppointmentCTA={scheduleAppointmentCTA}
                hasScheduleAppointmentCTA={hasScheduleAppointmentCTA}
                fixedBarVisible={floatingBarVisible}
                fixedBarStyle={fixedBarStyle}
                animatedRef={animatedRef}
                useDisplayNoneWhenHidden={false}
                onContactClick={handleContactClick}
                onCTAClick={handleCTAClick}
                onScheduleAppointmentCTAClick={
                  handleScheduleAppointmentCTAClick
                }
              />
            )}
          </>
        )}
      <div ref={contactBarRef} data-sub-navigation-contact-bar>
        <ContactBarOrganism
          salesCenterVM={salesCenterVM}
          titleCtaVM={{ firstCtaLink, secondCtaLink, title }}
          secondSalesCenterVM={secondSalesCenterVM}
          scheduleAppointmentCtaVM={scheduleAppointmentCtaVM}
          marketSummaryVM={marketSummaryVM}
        />
      </div>
    </>
  );
};

export default ContactBar;
